import axios from 'axios'
import { onAuthStateChanged, getAuth } from 'firebase/auth'

// 🔽 RTDB for status mirroring
import { database } from '@/firebase'
import { ref as rtdbRef, set as rtdbSet } from 'firebase/database'
import { getSubdomain } from '@/composables/useTheme'

const api = axios.create({
  baseURL: 'https://api.amarel.app/api'
})

const subdomain = getSubdomain()

// GROUPS
export const createGroups = (payload) => api.post('/group/v2/create', payload)
export const getGroupsByEvent = (eventId) => api.get(`/group/${eventId}`)
export const getAllGroupsCompatibility = (eventId) =>
  api.get(`/group/${eventId}/compatibility`)

const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    const auth = getAuth()
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        unsubscribe()
        if (user) {
          try {
            const token = await user.getIdToken()
            resolve(token)
          } catch (err) {
            reject(err)
          }
        } else {
          resolve(null)
        }
      },
      reject
    )
  })

function resolveSubdomainHeader() {
  const cached = sessionStorage.getItem('selectedSubdomain')
  if (cached) return cached
  const hostname = window.location.hostname || ''
  if (!hostname || hostname.includes('localhost')) return ''
  const parts = hostname.split('.')
  if (parts.length > 2) {
    return parts[0]
  }
  return ''
}

api.interceptors.request.use(async (config) => {
  const token = await getFirebaseToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const subdomain = resolveSubdomainHeader()
  if (subdomain) {
    config.headers['X-Client-Subdomain'] = subdomain
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      throw {
        status: error.response.status,
        error: error.response.data?.error,
        message: error.response.data?.message
      }
    }
  }
)

export const getEvents = async () => {
  const data = await api.get('/events', { params: { subdomain } })
  return data
}

export const createEvent = async (payload) => {
  const data = await api.post('/event', {
    ...payload,
    subdomain
  })
  return data
}

export const updateEvent = async (eventId, payload) => {
  const data = await api.put(`/event/${eventId}`, payload)
  return data
}

export const getEvent = async (eventId) => {
  const data = await api.get('/event', {
    params: {
      event_id: eventId
    }
  })
  return data
}

// 🔸 Also publish status to Firebase RTDB at events/{eventId}/status
export const setEventStatus = async (eventId, status) => {
  const numericStatus = typeof status === 'number' ? status : Number(status)

  // 1) Update backend
  const res = await api.put(`/event/${eventId}/status`, {
    status: numericStatus
  })

  // 2) Mirror to RTDB (non-blocking for UI)
  try {
    await rtdbSet(rtdbRef(database, `events/${eventId}/status`), numericStatus)
  } catch (err) {
    console.error('Failed to mirror status to RTDB:', err)
  }

  return res
}

export const getHistoryData = async (eventId) => {
  const data = await api.get('/histories', {
    params: {
      event_id: eventId
    }
  })
  return data
}

export const getAttendeesByEventId = async (eventId = undefined) => {
  const data = await api.get('/attendees', {
    params: {
      event_id: eventId,
      isReturnAll: true
    }
  })
  return data
}

export const getAttendees = async ({
  page,
  name,
  limit,
  eventId,
  gender,
  age,
  status
}) => {
  const data = await api.get('/attendees', {
    params: {
      page,
      name,
      limit,
      event_id: eventId,
      gender,
      age,
      status,
      subdomain
    }
  })
  return data
}

export const generateAttendeeTopMatches = async ({
  attendee_id,
  is_show_reason,
  language,
  matching_gender
}) => {
  const data = await api.post('/matching-top', {
    attendee_id,
    is_show_reason,
    language,
    matching_gender
  })
  return data
}

export const getAttendee = async (id) => {
  const data = await api.get('/attendee', {
    params: {
      id
    }
  })
  return data
}

// 🔹 NEW: Update attendee (PUT /attendee/:id)
// Sends only defined fields; both camelCase and snake_case are accepted by the API.
export const updateAttendee = async (id, payload) => {
  // Remove undefined values so we don't accidentally send them
  const body = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  )

  const data = await api.put(`/attendee/${id}`, body)
  return data
}

export const startMatchingAttendees = async (payload) => {
  const data = await api.post(`/matching`, payload)
  return data
}

export const getMatchingResults = async ({
  event_id,
  page,
  limit,
  percentage,
  age,
  status,
  name
}) => {
  const data = await api.get('/matching-results', {
    params: {
      page,
      limit,
      event_id,
      percentage,
      age,
      status,
      name,
      subdomain
    }
  })
  return data
}

export const getRecentActivities = async () => {
  const data = await api.get('/dashboard/histories', {
    params: {
      subdomain
    }
  })
  return data
}

export const getMatchDetail = async ({ event_id, attendee_id }) => {
  const data = await api.get('/matching-result', {
    params: {
      event_id,
      attendee_id
    }
  })
  return data
}

export const getAttendeeTopMatches = async (attendee_id) => {
  const data = await api.get('/matching-top-result', {
    params: {
      attendee_id
    }
  })
  return data
}

export const getClients = async () => {
  const data = await api.get('/clients')
  return data
}

export const getClientDetail = async (clientId) => {
  const data = await api.get(`/client/${clientId}`)
  return data
}

export const getClientAdmins = async (clientId) => {
  const data = await api.get(`/client-admin/${clientId}`)
  return data
}

export const createClientAdmin = async (payload) => {
  const data = await api.post('/client-admin', payload)
  return data
}

export const updateClientAdmin = async (id, payload) => {
  const data = await api.put(`/client-admin/${id}`, payload)
  return data
}

export const deleteClientAdmin = async (id) => {
  const data = await api.delete(`/client-admin/${id}`)
  return data
}

export const getCurrentClientAdmin = async () => {
  const data = await api.get('/client-admin/me')
  return data
}

export const updateClient = async (clientId, payload) => {
  return await api.put(`/client/${clientId}`, payload)
}

export const deleteClient = async (clientId) => {
  return await api.delete(`/client/${clientId}`)
}

export const createClient = async (payload) => {
  return await api.post(`/client`, payload)
}

export const getClientTheme = async (subdomain) => {
  return await api.get(`/client/subdomain/${subdomain}`)
}
